import useToastStore from "@/shared/zustand/toastStore";
import { HOST_MODES, MIME_TYPE_EXTENSIONS } from "@/constants";
import * as utf8 from "utf8";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import CryptoJS from "crypto-js";
import useAuthStore from "../zustand/authStore";
import { getUserSession } from "@/core/api/api";
import Cookies from "js-cookie";
export function cn(...inputs) {
    return twMerge(clsx(inputs));
}
export const getHtmlMarkup = (content) => {
    const lines = content.split("\n");
    let htmlContent = "";
    lines.forEach((line) => {
        let element = "";
        if (line.startsWith("# ")) {
            element = `<br><h1>${line.substring(2)}</h1>`;
        }
        else if (line.startsWith("## ")) {
            element = `<br><h2>${line.substring(3)}</h2>`;
        }
        else if (line.startsWith("### ")) {
            element = `<br><h3>${line.substring(4)}</h3>`;
        }
        else if (line.startsWith("#### ")) {
            element = `<br><h4>${line.substring(4)}</h4>`;
        }
        else if (line.startsWith("- ")) {
            element = `<ul><li>${line.substring(2)}</li></ul>`;
        }
        else if (/^\d+\./.test(line)) {
            element = `<ol><li>${line.substring(3)}</li></ol>`;
        }
        else if (line.startsWith("*")) {
            element = `<p class="privacy">${line}</p>`;
        }
        else if (line.trim() !== "") {
            element = `<p style={{font-size: "1.2rem"}}>${line}</p>`;
        }
        htmlContent += element;
    });
    return htmlContent;
};
export const isMarkdownFormat = (text) => {
    // Define regular expressions for common Markdown patterns
    const markdownPatterns = [
        /^#{1,6}\s.+/m, // Headers (# Header)
        /\*\*[^*]+\*\*/m, // Bold (**text**)
        /\*[^*]+\*/m, // Italic (*text*)
        /!\[.*\]\(.*\)/m, // Images (![alt](url))
        /\[.*\]\(.*\)/m, // Links ([text](url))
        /^>\s.+/m, // Blockquotes (> quote)
        /^-+\s*$/m, // Horizontal rules (---)
        /^(\*|\-|\+)\s.+/m, // Unordered lists (* item)
        /^\d+\.\s.+/m, // Ordered lists (1. item)
        /`{1,3}[^`]+`{1,3}/m, // Inline code (`code`)
        /^```[\s\S]+?^```$/m, // Code blocks (``` code ```)
        /\|.*\|/m, // Tables (| Header |)
    ];
    // Check if any of the patterns match the text
    for (const pattern of markdownPatterns) {
        if (pattern.test(text)) {
            return true;
        }
    }
    return false;
};
export const formatTimeDisplay = (timeString) => {
    try {
        // Handle special case for exactly 00:00:00 or 00:00:00.000
        if (timeString === "00:00:00" || timeString === "00:00:00.000") {
            return "00:00";
        }
        const [timeComponent, milliseconds] = timeString.split(".");
        const [hours, minutes, seconds] = timeComponent.split(":").map(Number);
        if (hours > 0) {
            return `${hours.toString().padStart(2, "0")}:${minutes
                .toString()
                .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}${milliseconds ? `.${milliseconds}` : ""}`;
        }
        if (minutes > 0) {
            return `${minutes.toString().padStart(2, "0")}:${seconds
                .toString()
                .padStart(2, "0")}${milliseconds ? `.${milliseconds}` : ""}`;
        }
        if (seconds === 0 && milliseconds) {
            return `00:0.${milliseconds}`;
        }
        return `00:${seconds.toString().padStart(2, "0")}${milliseconds ? `.${milliseconds}` : ""}`;
    }
    catch (error) {
        console.error("Time formatting error:", error);
        return timeString;
    }
};
export const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const millisecs = Math.floor((seconds % 1) * 1000);
    const hoursStr = hours.toString().padStart(2, "0");
    const minutesStr = minutes.toString().padStart(2, "0");
    const secondsStr = secs.toString().padStart(2, "0");
    const millisecsStr = millisecs.toString().padStart(3, "0");
    // console.log("time is", `${hoursStr}:${minutesStr}:${secondsStr}.${millisecsStr}`);
    return `${hoursStr}:${minutesStr}:${secondsStr}.${millisecsStr}`;
};
export function replaceAsteriskInAnchor(inputString) {
    // to match an anchor tag with asterisk inside
    const regex = /<a [^>]*>.*?<\/a>/g;
    // Check if the inputString contains an anchor tag with asterisk and replace it
    const replacedString = inputString.replace(regex, (match) => {
        // Replace asterisk inside the anchor tag with &ast;
        return match.replace(/\*/g, "&ast;");
    });
    return replacedString;
}
export function extractWaitMessage(inputString) {
    const prefix = "WaitMessage: ";
    const startIndex = inputString.indexOf(prefix);
    if (startIndex !== -1) {
        const extractedString = inputString.substring(startIndex + prefix.length);
        return extractedString.replace("WaitMessage: ", "");
    }
    else {
        return "";
    }
}
export const encodeParam = (param) => {
    return utf8.encode(param);
};
export function convertMessagesToChatMessages(messages) {
    return messages
        .filter((message) => message.status !== "loading")
        .map((message) => {
        const sender = message.role;
        const timestamp = new Date().toISOString();
        const content = message.content.split("\n");
        return {
            sender: sender,
            message: content,
            timestamp: timestamp,
        };
    });
}
// Helper functions
export const getFileExtension = (mimeType) => {
    // Handle image types separately
    if (mimeType.startsWith("image/")) {
        return "." + mimeType.split("/")[1];
    }
    // Check in predefined mapping
    const extension = MIME_TYPE_EXTENSIONS[mimeType];
    if (extension) {
        return extension;
    }
    // Handle video types
    if (mimeType.startsWith("video/")) {
        return "." + mimeType.split("/")[1];
    }
    // Handle audio types
    if (mimeType.startsWith("audio/")) {
        return "." + mimeType.split("/")[1];
    }
    // Default to binary for unknown types
    return ".bin";
};
export const generateUniqueFileName = (mimeType, fileName) => {
    const timestamp = new Date().getTime();
    // const randomString = Math.random().toString(36).substring(2, 8);
    // return `file_${fileName ?? timestamp}_${randomString}${getFileExtension(mimeType)}`;
    return fileName ?? `${`Untitled_`}${timestamp}`;
};
export function isStreamingMessageArray(data) {
    return (Array.isArray(data) &&
        data.every((msg) => typeof msg === "object" &&
            msg !== null &&
            "role" in msg &&
            "content" in msg));
}
export const isArtifactDataFilename = (filename) => {
    if (!filename)
        return false;
    // pattern to match Artifact_[title]_[id].artifact.json
    const regex = /^Artifact_.*_[a-zA-Z0-9-_]+\.artifact\.json$/;
    return regex.test(filename);
};
export const isProjectFilePath = (path) => {
    if (!path)
        return false;
    const regex = /^client-creative-workspace\/projects\/[a-zA-Z0-9]+\/.*$/;
    return regex.test(path);
};
export const formatTimestamp = (timestamp) => {
    if (!timestamp)
        return "Not available";
    try {
        // Parse the ISO timestamp string directly
        const date = new Date(timestamp);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            return "Invalid date";
        }
        // Format the date
        const formattedDate = new Intl.DateTimeFormat("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(date);
        // Format the time
        const formattedTime = new Intl.DateTimeFormat("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        }).format(date);
        return `${formattedDate} at ${formattedTime}`;
    }
    catch (error) {
        console.error("Error formatting timestamp:", error);
        return "Date formatting error";
    }
};
// Helper function to trigger file download
export const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    // Clean up
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
};
export const isAccessTokenExpired = (expiryTimestamp, minutes = 45) => {
    // Convert timestamp string to number
    const expiryTime = parseInt(expiryTimestamp, 10);
    // Validate timestamp format
    if (isNaN(expiryTime) || expiryTimestamp.length !== 10) {
        throw new Error("Invalid timestamp format. Expected a 10-digit string.");
    }
    // Get current time in seconds (Unix timestamp)
    const currentTime = Math.floor(Date.now() / 1000);
    // Calculate specified minutes in seconds
    const minutesInSeconds = minutes * 60;
    // Calculate the time specified minutes from now
    const futureTime = currentTime + minutesInSeconds;
    // Return true if the expiry time is between now and specified minutes from now
    return expiryTime > currentTime && expiryTime <= futureTime;
};
export function createHmacSignature(key, message) {
    const hmac = CryptoJS.HmacSHA256(message, key);
    return hmac.toString(CryptoJS.enc.Base64);
}
// function to get google access_token, if it is expired fetch a new one from session api or else return current one
export const getLatestUserDetails = async (user) => {
    try {
        if (!user || !user.provider?.expires_at) {
            console.warn("User or token expiry time is missing.");
            return;
        }
        const isExpired = isAccessTokenExpired(user.provider.expires_at.toString(), 45);
        if (!isExpired) {
            console.info("Access token is still valid.");
            return user;
        }
        console.warn("Access token expired, fetching new session...");
        // Fetch a new session
        const { responseData, error } = await getUserSession();
        if (error) {
            console.error("Error refreshing session:", error);
            useToastStore
                .getState()
                .showToast("error", "Session Error", "Could not refresh session. Please log in again.");
            useAuthStore.getState().logout(); // Log out user if session fails
            return;
        }
        if (responseData?.user) {
            console.log("Session refreshed successfully.");
            useAuthStore.getState().setUser(responseData.user); // Update the user state with new token
            return responseData?.user;
        }
        else {
            console.error("Invalid session response:", responseData);
            useToastStore
                .getState()
                .showToast("error", "Session Error", "Session data is missing.");
            useAuthStore.getState().logout(); // Log out user if session data is missing
            return;
        }
    }
    catch (error) {
        console.error("Unexpected error in updateAccessToken:", error);
        useToastStore
            .getState()
            .showToast("error", "Error", "An unexpected error occurred.");
        useAuthStore.getState().logout();
        return;
    }
};
export const getCookie = (name) => {
    const userData = Cookies.get(name);
    console.log("userData", userData);
    return userData ? JSON.parse(userData) : null;
};
export function isHostMode(modeName) {
    return Object.keys(HOST_MODES).includes(modeName);
}
export const pareseDataFromMsg = (content) => {
    // If content is already an object, return it as is
    if (typeof content !== "string") {
        return content;
    }
    try {
        // Find the first { and last }
        const jsonStartIndex = content.indexOf("{");
        const jsonEndIndex = content.lastIndexOf("}") + 1;
        if (jsonStartIndex !== -1 && jsonEndIndex !== -1) {
            // Extract JSON string
            const jsonString = content.substring(jsonStartIndex, jsonEndIndex);
            // Clean the string of any control characters
            const sanitizedString = jsonString.replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
            // Parse the sanitized JSON
            return JSON.parse(sanitizedString);
        }
        // If no JSON found, return the original content
        return content;
    }
    catch (error) {
        console.error("Error processing JSON data:", error);
        // If parsing fails, return the original content
        return {};
    }
};
export function checkModeSpecificScopes(userScope, modesScope) {
    const userScopesList = userScope.split(" ");
    const modesScopesList = modesScope.split(" ");
    return modesScopesList.some(scope => userScopesList.includes(scope));
}
